import {
  Badge,
  Box,
  Card,
  Flex,
  Group,
  Image,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  Title,
  rem,
} from "@mantine/core";
import { IconCheck, IconX } from "@tabler/icons-react";
import React from "react";

import { COLOR_VISION_LEGAND } from "@constants/uiConstants";
import { colorVisionValues } from "@constants/testConstants/testSpecificConstants/colorVisionConstants";
import { colorVisionPlateThumbImages } from "@utils/test-specific-utils/ColorVisionImageUtils";
import { useTranslation } from "react-i18next";

export default function ColorVisionResultCard(props) {
  const { results, current, cols } = props;
  const { t } = useTranslation();

  const displayResult = (ind, val, result, current?) => {
    const isCorrect = typeof result === "boolean" ? result : (result?.isCorrect ?? null);
    const patientResponse =
      isCorrect === false && result && typeof result === "object" && "response" in result
        ? result.response
        : undefined;
    return (
      <Flex justify={"flex-start"} align={"center"} w={"100%"} py={"md"}>
        <Stack gap={"xs"}>
          <Badge color={current === ind ? "orange.6" : "gray.6"} size="xl" circle>
            {ind + 1 + "."}
          </Badge>
        </Stack>
        <Stack gap={"xs"} align="center">
          <Text size="xs" c="dimmed">
            {t("colorVisionExpectedLabel")}
          </Text>
          <Badge size="xl" circle color={"blue.6"}>
            {COLOR_VISION_LEGAND[val] || val}
          </Badge>
          <ThemeIcon
            variant="filled"
            color={isCorrect !== null ? (isCorrect === true ? "green" : "red") : "gray"}
            size={"lg"}
            radius="xl"
          >
            {isCorrect !== null ? (
              isCorrect === true ? (
                <IconCheck style={{ width: "70%", height: "70%" }} stroke={1.5} />
              ) : (
                <IconX style={{ width: "70%", height: "70%" }} stroke={1.5} />
              )
            ) : null}
          </ThemeIcon>
        </Stack>
        {patientResponse !== undefined && (
          <Stack gap={"xs"} align="center">
            <Text size="xs" c="dimmed">
              {t("colorVisionPatientResponseLabel")}
            </Text>
            <Badge size="xl" circle color={"red.6"}>
              {patientResponse === "" || patientResponse == null
                ? t("colorVisionNoResponse")
                : (COLOR_VISION_LEGAND[patientResponse] ?? patientResponse)}
            </Badge>
          </Stack>
        )}
        <Box
          style={{
            width: 120,
            height: 120,
            borderRadius: "50%",
            overflow: "hidden",
            flexShrink: 0,
          }}
        >
          <Image
            src={colorVisionPlateThumbImages[ind]}
            alt={`Ishihara Plate Result ${ind + 1}`}
            width={120}
            height={120}
            fit="cover"
          />
        </Box>
      </Flex>
    );
  };

  return (
    <Card withBorder radius="md" shadow="md">
      <Flex justify={"space-between"} align={"center"}>
        <Title order={2}>{t("Results")}</Title>
        <SimpleGrid cols={3} spacing="0" verticalSpacing={rem(3)}>
          {Object.keys(COLOR_VISION_LEGAND).map((item, i) => (
            <Group key={"cv-legand-" + i} gap={"xs"} align={"center"}>
              <Badge color={"blue.6"} size={"lg"} circle>
                {COLOR_VISION_LEGAND[item]}
              </Badge>
              <Text fz={"md"} c="dimmed">
                {t("ColorVision." + item)}
              </Text>
            </Group>
          ))}
        </SimpleGrid>
      </Flex>
      <SimpleGrid cols={cols ?? 3} spacing={"xs"}>
        {results.map((result, i) => (
          <Box key={"cv-result-" + i}>
            {displayResult(i, colorVisionValues[i + 1], result, current)}
          </Box>
        ))}
      </SimpleGrid>
    </Card>
  );
}
